import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(248, 250, 252, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(20px)' : 'none'};
  border-bottom: ${props => props.scrolled ? `1px solid ${corporateTheme.cardBorder}` : 'none'};
  transition: all 0.3s ease;
  padding: 1rem 0;
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
`;

const Logo = styled(motion.div)`
  font-size: 2rem;
  font-weight: 800;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  font-family: ${corporateTheme.fontFamily};
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: rgba(248, 250, 252, 0.98);
    flex-direction: column;
    padding: 2rem;
    border-top: 1px solid ${corporateTheme.cardBorder};
    box-shadow: ${corporateTheme.cardShadow};
  }
`;

const NavLink = styled(motion.a)`
  color: ${corporateTheme.textPrimary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
    color: ${corporateTheme.primary};
  }
`;

const StyledNavLink = styled(motion.div)`
  color: ${corporateTheme.textPrimary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
    color: ${corporateTheme.primary};
  }
`;

const CTAButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(30, 64, 175, 0.3);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  cursor: pointer;
  font-weight: 600;
  padding: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#hero', type: 'hash' },
    { label: 'Services', href: '#services', type: 'hash' },
    { label: 'Features', href: '#features', type: 'hash' },
    { label: 'Why Choose Us', href: '#why-choose', type: 'hash' },
    { label: 'About', href: '/about', type: 'route' },
    { label: 'Contact', href: '/contact', type: 'route' }
  ];

  return (
    <NavContainer
      scrolled={scrolled}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <NavContent>
        <RouterLink to="/" style={{ textDecoration: 'none' }}>
          <Logo
            whileHover={{ scale: 1.05 }}
          >
            GrowTech
          </Logo>
        </RouterLink>

        <NavLinks isOpen={mobileOpen}>
          {navItems.map((item, index) => (
            item.type === 'route' ? (
              <RouterLink
                key={index}
                to={item.href}
                style={{ textDecoration: 'none' }}
                onClick={() => setMobileOpen(false)}
              >
                <StyledNavLink
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                </StyledNavLink>
              </RouterLink>
            ) : (
              <NavLink
                key={index}
                href={item.href}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </NavLink>
            )
          ))}
          
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const contactSection = document.querySelector('#contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                window.location.href = '/contact';
              }
              setMobileOpen(false);
            }}
          >
            {corporateLabels.getStarted}
          </CTAButton>
        </NavLinks>

        <MobileToggle onClick={() => setMobileOpen(!mobileOpen)}>
          MENU
        </MobileToggle>
      </NavContent>
    </NavContainer>
  );
};

export default Navigation; 